import type { ButtonHTMLAttributes, FC, ReactNode } from 'react';

/**
 * How much weight a button carries.
 *
 * Only one `primary` should be visible at a time — it is the single dark mark on an otherwise
 * neutral page, so a second one costs the first its meaning.
 */
export type ActionButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';

/** The prototype's button colours; focus is the global cyan outline (`styles/index.css`). */
const VARIANT_CLASSES: Record<ActionButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-deep',
  secondary: 'border border-control bg-white text-muted hover:border-primary hover:text-primary',
  ghost: 'text-muted hover:bg-surface hover:text-primary',
  danger: 'bg-danger text-white hover:bg-danger-deep',
  'danger-ghost': 'text-muted hover:bg-danger-soft hover:text-danger',
};

/** Shared by every variant: the prototype's 4 px corner, semibold label and the disabled state. */
const BASE_CLASSES =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded font-semibold disabled:pointer-events-none disabled:opacity-50';

interface ActionButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ActionButtonVariant;
  /** Leading icon. Omit it and the button is label-only. */
  icon?: ReactNode;
  /** Swaps the icon for a spinner and blocks interaction. */
  loading?: boolean;
  /** Square icon-only sizing; the caller still has to supply an `aria-label`. */
  iconOnly?: boolean;
  /**
   * `sm` is the toolbar button. `md` steps the label up to `text-base` — and an icon-only button
   * up to `h-9 w-9` — for the one action a screen is built around.
   */
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
}

/**
 * The one button in the app.
 *
 * PrimeReact's `Button` arrives wearing the bootstrap-blue theme, which is what put a different
 * blue on every action of every screen. This replaces it wherever the app owns the markup —
 * pages, dialogs, the header — so the accent is decided here and nowhere else.
 */
export const ActionButton: FC<ActionButtonProps> = ({
  variant = 'secondary',
  icon,
  loading = false,
  iconOnly = false,
  size = 'sm',
  className = '',
  children,
  disabled,
  type = 'button',
  ...rest
}) => {
  // An icon-only button is sized by its square, so `size` moves the box; a label button keeps the
  // same box and `size` moves the type. Both default to the toolbar's `sm`.
  const sizeClasses = iconOnly
    ? size === 'md'
      ? 'h-9 w-9 text-[13px]'
      : 'h-[29px] w-[29px] text-[12.5px]'
    : size === 'md'
      ? 'px-4 py-[9px] text-[13px]'
      : 'px-3 py-[7px] text-[12px]';
  const leading = loading ? (
    <span
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  ) : (
    icon
  );

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`${BASE_CLASSES} ${VARIANT_CLASSES[variant]} ${sizeClasses} ${className}`.trim()}
      {...rest}
    >
      {leading}
      {children}
    </button>
  );
};
